import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { artikelen } from "@/data/kennisbank";

export default function KennisbankArtikel() {
  const { slug } = useParams();
  const artikel = artikelen.find((a) => a.slug === slug);
  usePageMeta({
    title: artikel ? `${artikel.titel}, Kennisbank LevelLeven` : "Kennisbank",
    description: artikel?.subtitel,
  });

  if (!artikel) return <Navigate to="/kennisbank" replace />;

  const gerelateerd = artikelen.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div>
      <section className="py-16 sm:py-24 border-b border-[#E5E1D8]">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <Link
            to="/kennisbank"
            className="inline-flex items-center gap-2 text-sm text-[#7A8276] hover:text-[#1D2A22] mb-10 transition-colors"
          >
            <ArrowLeft size={14} /> Alle artikelen
          </Link>
          <SectionLabel>{artikel.thema} · Kennisbank</SectionLabel>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1D2A22] font-light"
          >
            {artikel.titel}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-xl text-[#4A5D4E] leading-relaxed font-light"
          >
            {artikel.subtitel}
          </motion.p>
          <div className="mt-8 flex items-center gap-6 text-xs uppercase tracking-[0.25em] text-[#7A8276]">
            <span>{artikel.datum}</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} /> {artikel.leesduur}
            </span>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10">
          <div
            data-testid="kb-artikel-body"
            className="text-lg text-[#1D2A22] leading-relaxed font-light space-y-10"
          >
            <p className="text-xl italic text-[#4A5D4E] border-l-2 border-[#E06B3F] pl-6 py-1">
              {artikel.intro}
            </p>
            {artikel.body.map((section, i) => (
              <div key={i}>
                <h2 className="font-heading text-3xl text-[#1D2A22] leading-tight mb-4">
                  {section.h}
                </h2>
                <p className="text-[#4A5D4E]">{section.p}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-[#E5E1D8]">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-5">Bronnen</p>
            <ol className="space-y-3 text-sm text-[#4A5D4E] leading-relaxed" data-testid="kb-artikel-bronnen">
              {artikel.bronnen.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#7A8276]">{String(i + 1).padStart(2, "0")}</span>
                  <span>{b}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-16 rounded-3xl bg-[#EFECE5] border border-[#E5E1D8] p-8 sm:p-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] mb-3">
              In de praktijk brengen
            </p>
            <p className="font-heading text-3xl text-[#1D2A22] leading-tight font-light">
              Wilt u dit vertalen naar uw gemeente?
            </p>
            <p className="mt-4 text-[#4A5D4E]">
              Plan een oriënterend gesprek, we lopen samen door welke inzichten
              van toepassing zijn op uw begroting en beleid.
            </p>
            <Link
              to="/contact"
              className="mt-6 group inline-flex items-center gap-3 bg-[#1D2A22] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-6 pr-2 py-2 text-sm transition-colors"
            >
              <span>Plan een gesprek</span>
              <span className="bg-[#E06B3F] group-hover:bg-[#FAF7F1] group-hover:text-[#1D2A22] text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                <ArrowUpRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {gerelateerd.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-[#E5E1D8] bg-[#EFECE5]/50">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
            <SectionLabel>Verder lezen</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {gerelateerd.map((a) => (
                <Link
                  key={a.slug}
                  to={`/kennisbank/${a.slug}`}
                  className="group bg-[#FAF7F1] rounded-2xl border border-[#E5E1D8] p-8 hover:border-[#E06B3F] transition-colors"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] mb-3">
                    {a.thema}
                  </p>
                  <p className="font-heading text-2xl text-[#1D2A22] leading-tight group-hover:text-[#E06B3F] transition-colors">
                    {a.titel}
                  </p>
                  <p className="mt-3 text-sm text-[#4A5D4E]">{a.subtitel}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
